import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ErrorObject } from 'context/ErrorContext';
import i18n from 'i18n/i18n';
import { ERROR_CODE, KnownError } from '../entities';

interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: any;
}

type HttpMethod = 'post' | 'get' | 'delete' | 'put' | 'patch';

const instance: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_DEV_URL || ''}/ApptSysPublicWeb/api/publicweb`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
  withCredentials: window.location?.hostname?.startsWith('localhost'),
});

const onRequest = (config: InternalAxiosRequestConfig) => {
  return config;
};

const onRequestError = (error: AxiosError, setError: (error: ErrorObject | null) => void) => {
  setError({ message: error.message || i18n.t('systemErrorDetail') });
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse, setError: (error: ErrorObject | null) => void) => {
  if (response.data.error) {
    const title =
      response.data.error.errorCode === ERROR_CODE.SYSTEM_UPDATE ? i18n.t('systemUpdateInProgress') : undefined;
    response.data.error.errorCode !== ERROR_CODE.INCORRECT_OTP &&
      response.data.error.errorCode !== ERROR_CODE.BOOKING_SERVICE_DISABLED &&
      setError({
        title,
        message: response.data.error.msg || i18n.t('systemErrorDetail'),
      });
    // return Promise.reject(response.data.error);
  }
  return response;
};

const onResponseError = (error: AxiosError, setError: (error: ErrorObject | null) => void) => {
  if (error) {
    setError(
      // Object.values((error.response?.data as any)?.errors)?.[0] ||
      { message: (error.response?.data as any)?.title || error.message || i18n.t('systemErrorDetail') },
    );
    return Promise.reject(error);
  }
};

const setupInterceptors = async (setError: (error: ErrorObject | null) => void) => {
  instance.interceptors.request.use(onRequest, (error: AxiosError) => onRequestError(error, setError));
  instance.interceptors.response.use(
    (response: AxiosResponse) => onResponse(response, setError),
    (error: AxiosError) => onResponseError(error, setError),
  );
};

export { instance, setupInterceptors };

const handleResponse = (response: any) => {
  if ('error' in response.data) return Promise.reject(response.data.error);
  return response.data.data;
};

export default function request<T>(
  method: HttpMethod,
  url: string,
  data: any = null,
  config?: AxiosRequestConfig,
): Promise<T> {
  switch (method) {
    case 'post':
      return instance.post(url, data, config).then(handleResponse);
    case 'get':
      return instance.get(url, { params: data, ...config }).then(handleResponse);
    case 'delete':
      return instance.delete(url, { params: data, ...config }).then(handleResponse);
    case 'put':
      return instance.put(url, data, config).then(handleResponse);
    case 'patch':
      return instance.patch(url, data, config).then(handleResponse);
  }
}

// A type guard to check if the response is an error
export const isKnownError = (data: any): data is KnownError => {
  return data && typeof data === 'object' && 'errorCode' in data;
};

export type SuccessCallback<T> = (res: T) => void;
export type ErrorCallback = (error: KnownError | DOMException) => void;
export const defaultErrorCallback: ErrorCallback = console.warn;

export const apiPost = <T, U>(
  endpoint: string,
  config: T,
  onSuccess: SuccessCallback<U>,
  onError: ErrorCallback,
  finallyCallback?: () => void,
) => request<U>('post', endpoint, config).then(onSuccess).catch(onError).finally(finallyCallback);
